// core/reshare-readiness.mjs — what must be ready before a listing is reshared to a portal.
// ⚠️ TWIN: server/lib/reshare_readiness.mjs must stay byte-identical (the test compares them).
// The Desk imports this file (ShareTab questions, planner); the server imports the twin (the
// readiness check an automated reshare runs before queueing a job). One rule set, two readers —
// a second copy of these rules is exactly what drifted before (2026-10-16: land plots asked
// apartment questions).
//
// SOURCE: each portal's own form code, read 2026-10-17 (korter realtyForm bundle, ss.ge _app
// yup schema module 59604, myhome statements SPA index bundle), cross-checked with the
// extension's fillers. Raw findings with code excerpts: core/RESHARE_READINESS.md.
// Only what a portal REFUSES to publish without is listed — not what it merely shows a * for.
//
// Payload vocabulary is the extension's (schema.js): dealType trade|lease|daily_rent|girao|ijara,
// propertyType apartment|house|commercial|land|hotel (+ scraper aliases below). Keys are the
// payload keys the fillers read. CHOICE keys (buildingStatus, condition, landStatus) are stored
// as tags in payload.tagKeys — ShareTab's missingValue knows that.

export const PORTALS = ['myhome', 'ss', 'korter']

const GROUP = {
  apartment: 'flat', penthouse: 'flat', studio: 'flat',
  house: 'house', cottage: 'house', townhouse: 'house', duplex: 'house', villa: 'house', agarak: 'house',
  land: 'land', hotel: 'hotel', commercial: 'commercial',
}
export const groupOf = pt => GROUP[pt] || null

const DEAL = { trade: 'sale', sale: 'sale', lease: 'rent', rent: 'rent', daily_rent: 'daily', girao: 'pledge', pledge: 'pledge', ijara: 'ijara' }
export const dealOf = dt => DEAL[dt] || null

// ss.ge requires a street (and, off land, a house number) only in these four cities (cityId 95–98).
const SS_STREET_CITIES = /თბილის|ბათუმ|ქუთაის|რუსთავ|tbilisi|batumi|kutaisi|rustavi/i
const cityIs = (re, city) => !city || re.test(String(city))

// What a portal does not offer at all for this deal × type. A reshare there cannot be made.
function unsupported(portal, deal, g) {
  if (portal === 'korter') {
    if (deal === 'ijara') return 'korter has no lease-out deal'
    if (g === 'land' && deal !== 'sale') return 'korter offers land for sale only'
    // A hotel is a korter COMMERCIAL sub-type (korter's own table, 2026-10-17), so it follows commercial.
    if ((g === 'commercial' || g === 'hotel') && deal === 'daily') return 'korter has no daily rent for commercial'
  }
  return null
}

// What the EXTENSION cannot fill yet for this deal × type — the portal would refuse it, and no
// answer in the Desk can help, so an automated reshare must not be queued (the agent can still
// finish it by hand on the PC). Remove an entry the day its filler learns the field.
function notAutomatable(portal, deal, g) {   // eslint-disable-line no-unused-vars -- kept for the next such field
  return null
}

/**
 * The payload keys that must hold a value before sharing `o` to `portal`.
 * Deal and type come first: until both are known nothing else can be decided, so only they
 * (and the place/price every portal needs) are asked.
 */
export function requiredFor(portal, o) {
  const g = groupOf(o?.propertyType)
  const keys = ['dealType', 'propertyType', 'city', 'streetName', 'price']
  const add = (...k) => k.forEach(x => { if (!keys.includes(x)) keys.push(x) })
  // The TYPE decides the questions; the deal only adds checks (blockers), so a known type with a
  // still-blank deal already gets its type's questions (the Desk asks the deal beside them).
  if (!g) return keys
  const area = g === 'land' ? 'landAreaSqM' : 'areaSqM'
  // HOUSE NUMBER ON ALL THREE, every type (owner 2026-10-14: "ss, korter and myhome ask street
  // number" — his own example was a land plot). The portals' own code demands it less often
  // (myhome: its server; ss: four cities; korter: a rented flat in Tbilisi/Batumi), but a repost
  // without one is a weaker ad and korter refuses an unknown typed number anyway.
  add('streetNumber')

  if (portal === 'myhome') {
    // myhome's page does not demand a house number, but its server refused a street without one
    // (2026-10-09, houseNo=null); a land plot rarely has one.
    add(area)
    if (g === 'land') add('landStatus')
    else {
      if (g !== 'commercial') add('buildingStatus')   // commercial's status row is its sub-type, picked from the type
      add('condition', 'roomsCount', 'totalFloors')
      if (g !== 'house') add('aptFloor')
    }
    // No description: myhome stars it but its form code does not enforce it, and ss/korter never ask
    // (owner 2026-10-17: "description is not required by any platform"). Most adverts carry one anyway.
  } else if (portal === 'ss') {
    // ss needs a street only in its four big cities; the other portals still need one, so the
    // union the Desk asks is unchanged — this only keeps an ss-only reshare from asking it.
    if (!cityIs(SS_STREET_CITIES, o?.city)) keys.splice(keys.indexOf('streetName'), 1)
    add(area)
    if (g === 'land') add('landStatus')
    else add('buildingStatus')
    if (g === 'flat') add('roomsCount', 'bedroomsCount', 'aptFloor', 'totalFloors')
    if (g === 'house') add('condition', 'landAreaSqM')
  } else if (portal === 'korter') {
    if (g === 'flat') add('roomsCount', 'aptFloor', 'totalFloors', 'areaSqM')   // bedrooms: the filler derives them
    if (g === 'house') add('roomsCount', 'totalFloors', 'areaSqM')
    if (g === 'commercial' || g === 'hotel') add('totalFloors', 'areaSqM')
  }
  return keys
}

const blank = v => v == null || (typeof v === 'string' && !v.trim())

// CHOICE fields are stored as the TAG the fillers read (mappings.js normal_keys) in payload.tagKeys,
// so "missing" means no tag of that vocabulary — not an empty payload key. Moved here from
// ShareTab 2026-10-17 so the SERVER judges a status/condition the same way: before, the server
// counted them as always present, and a reshare that never passed through the Desk (an automated
// one) would have reached the portal with no status and stopped there.
export const CHOICES = {
  buildingStatus: ['ახალი აშენებული', 'ძველი აშენებული', 'მშენებარე'],
  condition: ['ახალი რემონტით', 'ძველი რემონტით', 'მიმდინარე რემონტი', 'სარემონტო', 'თეთრი კარკასი', 'თეთრი პლიუსი', 'მწვანე კარკასი', 'შავი კარკასი'],
  landStatus: ['სასოფლო-სამეურნეო', 'არა სასოფლო-სამეურნეო', 'კომერციული', 'სპეციალური', 'საინვესტიციო', 'ფერმა'],
}
// Condition is also stated as payload.condition (server-normalised, core_reshare_payload
// conditionFromTags) and, in myhome captures, as myhome's OWN wording in tagKeys — either counts.
const MH_CONDITION_WORDS = ['ახალი გარემონტებული', 'ძველი გარემონტებული']
export function isMissing(k, o) {
  if (!CHOICES[k]) return blank(o?.[k])
  const tags = o?.tagKeys || []
  if (tags.some(tk => CHOICES[k].includes(tk))) return false
  if (k === 'condition' && (CHOICES.condition.includes(o?.condition) || tags.some(tk => MH_CONDITION_WORDS.includes(tk)))) return false
  // A land status may also arrive as a number (ss ids 1–6) or text on payload.landStatus.
  if (k === 'landStatus' && !blank(o?.landStatus)) return false
  return true
}

/**
 * Problems that are not a missing field: the deal/type cannot go to this portal, the extension
 * cannot finish it, or a present value will be refused. Each is { code, message, key? }.
 * `key` set = the agent can fix it by correcting that field.
 */
export function blockers(portal, o) {
  const out = []
  const g = groupOf(o?.propertyType), deal = dealOf(o?.dealType)
  // ⛔ NO PLEDGE, EVER (owner 2026-10-17: "we dont work on გირაო … we dont open coop or reshare any
  // of გირაო"). Not a portal limit — a business rule, so it blocks every portal and every share,
  // the PC included; the pledge fields the portals would need are deliberately never built.
  if (deal === 'pledge') return [{ code: 'no_pledge', message: 'senTavus does not reshare pledge (გირაო) listings' }]
  // ⛔ THE OWNER SAID "DON'T POST" (owner rule 2026-10-17). The server puts the owner's own words in
  // payload.noRepublish (core 3526 reshare_forbidden). Like pledge, it blocks everything, the PC too.
  // A HOLD (core 3533, brain decision reshare_hold — e.g. a pre-sale of houses not built yet) comes
  // through the same field prefixed "on hold:", and says its own reason instead of "the owner said".
  if (o?.noRepublish && /^on hold:/i.test(o.noRepublish)) return [{ code: 'reshare_hold', message: String(o.noRepublish) }]
  if (o?.noRepublish) return [{ code: 'no_republish', message: `the owner asked not to post this listing: "${o.noRepublish}"` }]
  if (g && deal) {
    const u = unsupported(portal, deal, g)
    if (u) out.push({ code: 'unsupported', message: u })
    const n = notAutomatable(portal, deal, g)
    if (n) out.push({ code: 'not_automatable', message: n })
  }
  const photos = (o?.imageUrls || []).length
  if (!photos && portal !== 'ss') out.push({ code: 'no_photos', message: `${portal} needs at least one photo` })
  if (portal === 'korter' && g === 'flat' && deal === 'rent' && photos > 0 && photos < 3)
    out.push({ code: 'few_photos', message: 'korter needs at least 3 photos for a flat to rent' })
  const price = Number(o?.price)
  if (portal === 'ss' && Number.isFinite(price) && price > 0) {
    // ss refuses a price that looks like a phone number, and implausibly low prices (USD).
    if (/^5\d{8}$/.test(String(Math.round(price)))) out.push({ code: 'price_phone', key: 'price', message: 'ss refuses a price that looks like a phone number' })
    const min = deal === 'sale' ? 100 : deal === 'daily' ? 4.01 : 10
    if (deal && (o?.priceUnit || o?.currency || 'USD') === 'USD' && price < min) out.push({ code: 'price_low', key: 'price', message: `ss refuses a ${deal} price under ${min} USD` })
  }
  // myhome's house number: at most 3 digits and 1 letter ("12/14", "1234" are refused).
  if (portal === 'myhome' && !blank(o?.streetNumber) && !/^\d{1,3}[ა-ჰa-zA-Z]?$/.test(String(o.streetNumber).trim()))
    out.push({ code: 'street_number_format', key: 'streetNumber', message: 'myhome accepts a house number of at most 3 digits and 1 letter' })
  // A floor below the total floors (myhome, ss and korter all refuse the reverse).
  const f = Number(o?.aptFloor), tf = Number(o?.totalFloors)
  if (Number.isFinite(f) && Number.isFinite(tf) && tf > 0 && f > tf)
    out.push({ code: 'floor_above_total', key: 'aptFloor', message: 'the floor is above the total floors' })
  return out
}

/** One call for automation: ready only when nothing is missing and nothing blocks. */
export function readiness(portal, o, missingFn = isMissing) {
  const missing = requiredFor(portal, o).filter(k => missingFn(k, o))
  const blocks = blockers(portal, o)
  return { portal, ready: !missing.length && !blocks.length, missing, blockers: blocks }
}
